'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import type { FairtradePowerRow } from '@/lib/fairtrade-catalog';
import {
  getFairtradeRowDisplayPrices,
  isFairtradeCollectionHub,
  type FairtradeCollectionPriceStats,
} from '@/lib/fairtrade-catalog';
import { showFairtradeCollectionToast } from '@/components/help-center/fairtrade/FairtradeWishlistToast';
import {
  computePowerCollectionStats,
  consumeIdbFallbackFlag,
  ensurePowerCollectionStorageReady,
  getPowerCollectionRepository,
} from '@/services/powerCollectionStorage';
import type {
  OwnedItem,
  PowerCollectionInput,
  PowerCollectionStats,
  WishItem,
} from '@/lib/power-collection-types';

type PowerCollectionContextValue = {
  ready: boolean;
  wishes: WishItem[];
  owned: OwnedItem[];
  wishIds: Set<number>;
  ownedIds: Set<number>;
  wishCount: number;
  ownedCount: number;
  stats: PowerCollectionStats;
  isWished: (powerId: number) => boolean;
  isOwned: (powerId: number) => boolean;
  addWishFromRow: (row: FairtradePowerRow) => Promise<void>;
  removeWish: (powerId: number) => Promise<void>;
  toggleWishFromRow: (row: FairtradePowerRow) => Promise<void>;
  markOwnedFromRow: (row: FairtradePowerRow) => Promise<void>;
  markOwnedFromWish: (wish: WishItem) => Promise<void>;
  removeOwned: (powerId: number) => Promise<void>;
};

const PowerCollectionContext = createContext<PowerCollectionContextValue | null>(null);

function rowToInput(
  row: FairtradePowerRow,
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): PowerCollectionInput {
  const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
  return {
    powerId: row.id,
    name: row.name,
    shortname: row.name,
    category: row.category,
    typeLabel: row.typeLabel,
    minXats: prices.minXats,
    maxXats: prices.maxXats,
    minDays: prices.minDays,
    maxDays: prices.maxDays,
  };
}

function wishToInput(wish: WishItem): PowerCollectionInput {
  return {
    powerId: wish.powerId,
    name: wish.name,
    shortname: wish.shortname,
    category: wish.category,
    typeLabel: wish.typeLabel,
    minXats: wish.savedMinXats,
    maxXats: wish.savedMaxXats,
    minDays: wish.savedMinDays,
    maxDays: wish.savedMaxDays,
  };
}

type PowerCollectionProviderProps = {
  children: ReactNode;
  /** Catálogo Fairtrade carregado — para stats e preços actuais. */
  catalogRows?: FairtradePowerRow[] | null;
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>;
};

export function PowerCollectionProvider({
  children,
  catalogRows = null,
  collectionPrices,
}: PowerCollectionProviderProps) {
  const [ready, setReady] = useState(false);
  const [wishes, setWishes] = useState<WishItem[]>([]);
  const [owned, setOwned] = useState<OwnedItem[]>([]);

  const refresh = useCallback(async () => {
    const repo = getPowerCollectionRepository();
    const [nextWishes, nextOwned] = await Promise.all([
      repo.listWishes(),
      repo.listOwned(),
    ]);
    setWishes(nextWishes);
    setOwned(nextOwned);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await ensurePowerCollectionStorageReady();
      if (cancelled) return;
      if (consumeIdbFallbackFlag()) {
        toast.warning('Coleção em memória temporária', {
          description:
            'O armazenamento local não está disponível neste navegador. Os dados podem perder-se ao fechar a página.',
          duration: 7000,
        });
      }
      await refresh();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const wishIds = useMemo(() => new Set(wishes.map((w) => w.powerId)), [wishes]);
  const ownedIds = useMemo(() => new Set(owned.map((o) => o.powerId)), [owned]);

  const currentPricesById = useMemo(() => {
    const map = new Map<number, { minXats: number; maxXats: number }>();
    for (const row of catalogRows ?? []) {
      const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
      map.set(row.id, { minXats: prices.minXats, maxXats: prices.maxXats });
    }
    return map;
  }, [catalogRows, collectionPrices]);

  const stats = useMemo(
    () =>
      computePowerCollectionStats(
        wishes,
        owned,
        catalogRows?.filter((r) => !isFairtradeCollectionHub(r)).length ?? 0,
        currentPricesById
      ),
    [wishes, owned, catalogRows, currentPricesById]
  );

  const isWished = useCallback((powerId: number) => wishIds.has(powerId), [wishIds]);
  const isOwned = useCallback((powerId: number) => ownedIds.has(powerId), [ownedIds]);

  const addWishFromRow = useCallback(
    async (row: FairtradePowerRow) => {
      if (ownedIds.has(row.id)) {
        showFairtradeCollectionToast({
          kind: 'already-owned',
          powerName: row.name,
          smileyCode: row.name,
        });
        return;
      }
      const repo = getPowerCollectionRepository();
      await repo.addWish(rowToInput(row, collectionPrices));
      await refresh();
      showFairtradeCollectionToast({
        kind: 'wish-added',
        powerName: row.name,
        smileyCode: row.name,
      });
    },
    [collectionPrices, ownedIds, refresh]
  );

  const removeWish = useCallback(
    async (powerId: number, powerName?: string) => {
      const repo = getPowerCollectionRepository();
      const wish = wishes.find((w) => w.powerId === powerId);
      const name = powerName || wish?.name || String(powerId);
      const smiley = wish?.shortname || name;
      await repo.removeWish(powerId);
      await refresh();
      showFairtradeCollectionToast({
        kind: 'wish-removed',
        powerName: name,
        smileyCode: smiley,
      });
    },
    [refresh, wishes]
  );

  const toggleWishFromRow = useCallback(
    async (row: FairtradePowerRow) => {
      if (wishIds.has(row.id)) {
        await removeWish(row.id, row.name);
        return;
      }
      await addWishFromRow(row);
    },
    [addWishFromRow, removeWish, wishIds]
  );

  const markOwnedFromRow = useCallback(
    async (row: FairtradePowerRow) => {
      const repo = getPowerCollectionRepository();
      await repo.markOwned(rowToInput(row, collectionPrices));
      await refresh();
      showFairtradeCollectionToast({
        kind: 'owned-added',
        powerName: row.name,
        smileyCode: row.name,
      });
    },
    [collectionPrices, refresh]
  );

  const markOwnedFromWish = useCallback(
    async (wish: WishItem) => {
      const row = catalogRows?.find((r) => r.id === wish.powerId);
      const repo = getPowerCollectionRepository();
      if (row) {
        await repo.markOwned(rowToInput(row, collectionPrices));
      } else {
        await repo.markOwned(wishToInput(wish));
      }
      await refresh();
      showFairtradeCollectionToast({
        kind: 'owned-added',
        powerName: row?.name || wish.name,
        smileyCode: row?.name || wish.shortname,
      });
    },
    [catalogRows, collectionPrices, refresh]
  );

  const removeOwned = useCallback(
    async (powerId: number) => {
      const repo = getPowerCollectionRepository();
      const item = owned.find((o) => o.powerId === powerId);
      const name = item?.name || String(powerId);
      const smiley = item?.shortname || name;
      await repo.removeOwned(powerId);
      await refresh();
      showFairtradeCollectionToast({
        kind: 'owned-removed',
        powerName: name,
        smileyCode: smiley,
      });
    },
    [owned, refresh]
  );

  const value = useMemo<PowerCollectionContextValue>(
    () => ({
      ready,
      wishes,
      owned,
      wishIds,
      ownedIds,
      wishCount: wishes.length,
      ownedCount: owned.length,
      stats,
      isWished,
      isOwned,
      addWishFromRow,
      removeWish,
      toggleWishFromRow,
      markOwnedFromRow,
      markOwnedFromWish,
      removeOwned,
    }),
    [
      ready,
      wishes,
      owned,
      wishIds,
      ownedIds,
      stats,
      isWished,
      isOwned,
      addWishFromRow,
      removeWish,
      toggleWishFromRow,
      markOwnedFromRow,
      markOwnedFromWish,
      removeOwned,
    ]
  );

  return (
    <PowerCollectionContext.Provider value={value}>
      {children}
    </PowerCollectionContext.Provider>
  );
}

export function usePowerCollection(): PowerCollectionContextValue {
  const ctx = useContext(PowerCollectionContext);
  if (!ctx) {
    throw new Error('usePowerCollection must be used within PowerCollectionProvider');
  }
  return ctx;
}
